import { useEffect, useRef, useState } from "react";
import "./ProcessingStepper.scss";

export type ProcessingStatus = "pending" | "failed" | "success";

type ProcessingStepState = "done" | "running" | "waiting" | "failed";

export interface ProcessingStepItem {
    key: string;
    label: string;
}

interface ProcessingStepperProps {
    status: ProcessingStatus;
    steps: ProcessingStepItem[];

    /**
     * Duration for:
     * 1. Record decision
     * 2. Prepare change
     * 4. Verify result
     *
     * The third step waits for the status prop.
     */
    stepDurationsMs?: {
        first?: number;
        second?: number;
        fourth?: number;
    };

    endProcess?: () => void;
}

interface StepItem {
    key: string;
    label: string;
}

// const STEPS: StepItem[] = [
//     {
//         key: "record-decision",
//         label: "Record Decision",
//     },
//     {
//         key: "prepare-change",
//         label: "Prepare Change",
//     },
//     {
//         key: "rollback-zscaler-change",
//         label: "Rollback Zscaler Change",
//     },
//     {
//         key: "verify-result",
//         label: "Verify Result",
//     },
// ];

const STEP_MARK: Record<ProcessingStepState, string> = {
    done: "✓",
    running: "○",
    waiting: "○",
    failed: "✕",
};

const STEP_TEXT: Record<ProcessingStepState, string> = {
    done: "DONE",
    running: "RUNNING",
    waiting: "WAITING",
    failed: "FAILED",
};

/**
 * Active step index:
 * 0 = Record Decision
 * 1 = Prepare Change
 * 2 = Rollback Zscaler Change
 * 3 = Verify Result
 * 4 = Entire process completed
 */
export function ProcessingStepper({
    status,
    steps,
    stepDurationsMs = {},
    endProcess,
}: ProcessingStepperProps) {
    const { first = 500, second = 2000, fourth = 1000 } = stepDurationsMs;

    const [activeStep, setActiveStep] = useState(0);

    const endProcessRef = useRef(endProcess);
    const processEndedRef = useRef(false);

    useEffect(() => {
        endProcessRef.current = endProcess;
    }, [endProcess]);

    const finishProcess = () => {
        if (processEndedRef.current) {
            return;
        }

        processEndedRef.current = true;
        endProcessRef.current?.();
    };

    /*
     * Step 1 completes after 500ms by default.
     */
    useEffect(() => {
        if (activeStep !== 0) {
            return;
        }

        const timer = window.setTimeout(() => {
            setActiveStep(1);
        }, first);

        return () => window.clearTimeout(timer);
    }, [activeStep, first]);

    /*
     * Step 2 completes after 2000ms by default.
     */
    useEffect(() => {
        if (activeStep !== 1) {
            return;
        }

        const timer = window.setTimeout(() => {
            setActiveStep(2);
        }, second);

        return () => window.clearTimeout(timer);
    }, [activeStep, second]);

    /*
     * Step 3 waits for the status prop.
     *
     * pending = keep running
     * failed  = mark step 3 as failed and finish
     * success = complete step 3 and start step 4
     */
    useEffect(() => {
        if (activeStep !== 2) {
            return;
        }

        if (status === "failed") {
            finishProcess();
            return;
        }

        if (status === "success") {
            setActiveStep(3);
        }
    }, [activeStep, status]);

    /*
     * Step 4 runs for 1000ms by default.
     */
    useEffect(() => {
        if (activeStep !== 3) {
            return;
        }

        const timer = window.setTimeout(() => {
            setActiveStep(4);
            finishProcess();
        }, fourth);

        return () => window.clearTimeout(timer);
    }, [activeStep, fourth]);

    const getStepState = (index: number): ProcessingStepState => {
        if (index < activeStep) {
            return "done";
        }

        if (index > activeStep || activeStep === 4) {
            return activeStep === 4 ? "done" : "waiting";
        }

        if (index === 2 && status === "failed") {
            return "failed";
        }

        return "running";
    };

    return (
        <ol
            className="processing-stepper"
            aria-label="Processing progress"
            aria-live="polite"
        >
            {steps.map((step, index) => {
                const stepState = getStepState(index);

                return (
                    <li
                        key={step.key}
                        className={`processing-step processing-step--${stepState}`}
                    >
                        <span
                            className="processing-step__mark"
                            aria-hidden="true"
                        >
                            {STEP_MARK[stepState]}
                        </span>

                        <span className="processing-step__label">
                            {step.label}
                        </span>

                        <span className="processing-step__status">
                            {STEP_TEXT[stepState]}
                        </span>
                    </li>
                );
            })}
        </ol>
    );
}

export default ProcessingStepper;
